// components/ErrorFallback.tsx
import { Button } from '@casumo/dojo-react';
import { Close } from '@casumo/dojo-react-icons';
import { useErrorBoundary } from 'react-error-boundary';
import useFetchErrorContent from '../api/hooks/useFetchErrorContent.tsx';
import { CasumoIcon, SuccessIcon, WarningIcon } from '../assets/icons';
import Dialog from '../design-system/dialog';
import { errorActions } from '../flow/errors/errorActions.ts';
import { FeErrorCode } from '../flow/errors/errorCodes.ts';
import { ErrorIcon, resolveErrorConfig } from '../flow/errors/errorRegistry.ts';

const ERROR_ICONS: Record<ErrorIcon, typeof WarningIcon> = {
  warning: WarningIcon,
  success: SuccessIcon,
};
import useMarketInfo from '../hooks/useMarketInfo';
import { useRegistrationStore } from '../store/store';
import { TEST_ID } from '../utils/constants';
import { FLOW_NAMES } from '../utils/countries';
import { interpolate } from '../utils/utils';

const getInterpolationValues = (cause: unknown): Record<string, string | number> => {
  if (!cause || typeof cause !== 'object') return {};

  return Object.entries(cause as Record<string, unknown>).reduce<Record<string, string | number>>(
    (acc, [key, value]) => {
      if (typeof value === 'string' || typeof value === 'number') acc[key] = value;

      return acc;
    },
    {},
  );
};

const ErrorFallback = ({ error }: { error: Error }) => {
  const { resetBoundary } = useErrorBoundary();
  const { setMetadata, clearFormData } = useRegistrationStore.getState();
  const marketInfo = useMarketInfo();

  const code = (error.name?.toLowerCase() || 'unknown_error') as FeErrorCode;
  const isPnp = marketInfo.flowName === FLOW_NAMES.MGA_GRA_PNP;
  const config = resolveErrorConfig(marketInfo.market, code, isPnp);

  const i18nKey = (config.i18nKey ?? code) as string;
  const BodyIcon = ERROR_ICONS[config.icon ?? 'warning'];

  const { data: msg, loading, fallbackTitle: apiFallbackTitle } = useFetchErrorContent(i18nKey);

  if (config.ignoreInBoundary) return null;

  if (loading) return <div data-testid={TEST_ID.ERROR + 'loading'}>Loading...</div>;

  const fallbackTitle = apiFallbackTitle ?? 'Something went wrong';

  const values = getInterpolationValues(error.cause);
  const title = msg?.title ? interpolate(msg.title, values, false) : undefined;
  const message = msg?.message ? interpolate(msg.message, values, false) : undefined;

  const ctx = {
    market: marketInfo.market,
    setMetadata,
    resetBoundary,
    clearFormData,
    marketInfo,
    error,
  };

  const run = (actionId?: keyof typeof errorActions) => () => {
    if (!actionId) return;
    errorActions[actionId](ctx);
  };

  return (
    <Dialog isOpen onClose={run('close_dialog')} overlayClass="bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="contents" data-testid={`error-${code}`}>
        <div className="flex items-center justify-center h-[72px] bg-white w-full border-b-2 border-gray-50">
          <h1 className="hidden md:flex text-h" data-testid={TEST_ID.ERROR + 'header-title'}>
            {title ?? fallbackTitle}
          </h1>
          <div className="md:hidden flex justify-between w-full text-h px-sm">
            <span className="w-md" />
            <CasumoIcon />
            <Close
              className="text-purple-60"
              onClick={run('close_dialog')}
              data-testid={TEST_ID.BUTTON_CLOSE}
            />
          </div>
        </div>

        <div className="flex flex-col h-full w-full overflow-hidden bg-white px-md py-2xl md:p-2xl">
          <BodyIcon data-testid={TEST_ID.ERROR + 'icon-' + (config.icon ?? 'warning')} />
          <div className="pt-md text-h-lg" data-testid={TEST_ID.ERROR + 'title'}>{title}</div>
          <div className="pt-sm text-body-base" data-testid={TEST_ID.ERROR + 'message'}>{message}</div>
        </div>

        <div className="flex flex-col gap-xs w-full bg-white justify-center items-center px-2xl pb-md">
          {msg?.btnSecondaryText && config.buttons?.secondary && (
            <Button
              className="w-full"
              variant="tertiary"
              onClick={run(config.buttons.secondary)}
              data-testid={TEST_ID.BUTTON_SECONDARY}
            >
              {msg.btnSecondaryText}
            </Button>
          )}
          {msg?.btnMainText && config.buttons?.primary && (
            <Button
              className="w-full"
              onClick={run(config.buttons.primary)}
              data-testid={TEST_ID.BUTTON_PRIMARY}
            >
              {msg.btnMainText}
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default ErrorFallback;
